/**
 * Funzioni e hook per la gestione dei processi attività
 * @module Pages/Admin/Processi/Functions
 */

import { useEffect, useState } from 'react'
import { FC, invalidateQuery, useCQuery } from '../../../Services'

/**
 * Hook per gestire lo stato di un processo
 * @param {string} processoId - ID del processo da caricare
 * @returns {Array} [processo, setProcessoProp, isSuccess]
 */
export const useProcesso = (processoId) => {
  const [processo, setProcesso] = useState()
  const { data, isSuccess } = useCQuery(['processiAttivita', processoId])

  useEffect(() => {
    if (isSuccess && data) {
      setProcesso(data)
    }
  }, [isSuccess, data])

  const setProcessoProp = (prop, value) =>
    setProcesso({ ...processo, [prop]: value })

  return [processo, setProcessoProp, isSuccess || processoId === 'new']
}

/**
 * Salva un processo (crea nuovo o aggiorna esistente)
 * @param {Object} processo - Oggetto processo da salvare
 * @returns {Promise<Object|boolean>} Risultato del salvataggio o false in caso di errore
 */
export const saveProcesso = async (processo) => {
  if (!processo?.codice) {
    window.growl.show({ severity: 'error', summary: 'Errore', detail: 'Inserire il codice' })
    return false
  }
  if (processo.id === 'new') {
    delete processo.id
    try {
      const res = await FC.service('processiAttivita').create({ ...processo })
      invalidateQuery(['processiAttivita'])
      window.growl.show({ severity: 'success', summary: 'Successo', detail: 'Processo creato' })
      return res
    } catch (e) {
      window.growl.show({ severity: 'error', summary: 'Errore', detail: 'Errore durante il salvataggio' })
      return false
    }
  }
  try {
    const res = await FC.service('processiAttivita').patch(processo.id, processo)
    invalidateQuery(['processiAttivita'])
    window.growl.show({ severity: 'success', summary: 'Successo', detail: 'Processo aggiornato' })
    return res
  } catch (e) {
    window.growl.show({ severity: 'error', summary: 'Errore', detail: 'Errore durante il salvataggio' })
    return false
  }
}

/**
 * Elimina (soft delete) un processo
 * @param {number} processoId - ID del processo da eliminare
 * @returns {Promise<Object>} Risultato dell'eliminazione
 */
export const deleteProcesso = async (processoId) => {
  const res = await FC.service('processiAttivita').patch(processoId, { deleted: true })
  invalidateQuery(['processiAttivita'])
  window.growl.show({ severity: 'success', summary: 'Successo', detail: 'Processo disabilitato' })
  return res
}

/**
 * Riabilita un processo precedentemente eliminato
 * @param {number} processoId - ID del processo da riabilitare
 * @returns {Promise<Object>} Risultato della riabilitazione
 */
export const restoreProcesso = async (processoId) => {
  const res = await FC.service('processiAttivita').patch(processoId, { deleted: false })
  invalidateQuery(['processiAttivita'])
  window.growl.show({ severity: 'success', summary: 'Successo', detail: 'Processo riabilitato' })
  return res
}
